import { z } from 'zod'

import { slugSchema } from '@scalar-org/entities/utility'

import { type Head, headSchema } from '@/schema/head'
import { pathSchema } from '@/schema/utility'

export type Redirect = {
  from: string
  to: string
}

export const redirectSchema = z.object({
  from: z.string(),
  to: z.string(),
}) satisfies z.ZodType<Redirect>

export type Routing = {
  guidePathPattern?: string
  referencePathPattern?: string
  redirects?: Redirect[]
}

export const routingSchema = z.object({
  guidePathPattern: z.string().optional(),
  referencePathPattern: z.string().optional(),
  redirects: redirectSchema
    .array()
    .optional()
    .describe(
      'Redirects for the site. Can be used to redirect from one path to another',
    ),
}) satisfies z.ZodType<Routing>

export type SiteConfig = {
  customDomain?: string
  subdomain?: string
  subpath?: string
  loginPortal?: string
  layout?: {
    toc?: boolean
    header?: boolean
    pageTitle?: boolean
    pageActions?: boolean
  }
  theme?: string
  logo?: string | { darkMode: string; lightMode: string }
  footer?: {
    filepath: string
    belowSidebar?: boolean
  }
  routing?: Routing
  head?: Head
}

export type Logo = string | { darkMode: string; lightMode: string }

export const logoSchema = z.union([
  z.string(),
  z.object({ darkMode: z.string(), lightMode: z.string() }),
]) satisfies z.ZodType<Logo>

export const siteConfigSchema = z
  .object({
    /** The custom domain of the site. This must have appropriate CNAME records set up as per dashboard.scalar.com instructions. */
    customDomain: z
      .string()
      .optional()
      .describe('The custom domain of the site'),
    /** The subdomain prefix of the site. Will be used to publish to <subdomain>.apidocumentation.com. This must be unique on the Scalar platform. */
    subdomain: z
      .string()
      .optional()
      .describe(
        'The subdomain prefix of the site. Will be used to publish to <subdomain>.apidocumentation.com. This must be unique on the Scalar platform.',
      ),
    subpath: pathSchema
      .optional()
      .describe(
        'The subpath of the site, e.g. /docs. All routes will be resolved against the subpath',
      ),
    /** The login portal slug for the site. References a created login portal from dashboard.scalar.com */
    loginPortal: slugSchema.optional(),
    /** Global layout options to hide/show select elements */
    layout: z
      .object({
        toc: z.boolean().optional(),
        header: z.boolean().optional(),
        pageTitle: z.boolean().optional(),
        pageActions: z.boolean().optional(),
      })
      .optional()
      .describe('Global layout options to hide/show select elements'),
    /** Slug for a platform defined theme */
    theme: z.string().optional().describe('Slug for a platform defined theme'),
    /** Site logo shown in the header. Can be single URL or an object with a URL for dark and light mode */
    logo: logoSchema
      .optional()
      .describe(
        'Site logo shown in the header. Can be single URL or an object with a URL for dark and light mode',
      ),
    /** Footer content. Can be a filepath to an HTML file, or raw HTML and CSS. */
    footer: z
      .object({
        belowSidebar: z
          .boolean()
          .optional()
          .describe(
            'Layout option to control if the footer pushes under the sidebar',
          ),
        filepath: z
          .string()
          .endsWith(
            '.html',
            'The footer filepath must point to a ".html" file.',
          )
          .describe('File path to the footer HTML file'),
      })
      .optional(),
    /** Routing options for the site */
    routing: routingSchema.optional(),
    /** Head options for the site (meta, scripts, etc-) */
    head: headSchema.optional(),
  })
  .default({}) satisfies z.ZodType<SiteConfig>
